import { FC } from 'react'
import classNames from 'classnames'

import Icon from 'project/Icon'
import Loader from './base/Loader'
import Switch from './base/forms/Switch'
import { components } from 'react-select'
import { openConfirm } from './base/Modal'
import Checkbox from './base/forms/Checkbox'
import InputGroup from './base/forms/InputGroup'
import { GroupMember, TeamMember } from 'common/types/responses'
import Select, { SelectComponents } from './base/forms/Select'
import { useGetTeamMembersQuery } from 'common/services/useTeamMember'
import { ErrorMessage } from './base/Messages'

type AddRemoveMembersType = {
  groupMembers: GroupMember[]
  setGroupMembers: (v: TeamMember[]) => void
}

const AddRemoveMembers: FC<AddRemoveMembersType> = ({
  groupMembers,
  setGroupMembers,
}) => {
  const { data, error, isFetching } = useGetTeamMembersQuery({ page_size: 100 })
  const teamMembers = data?.results

  const handleMemberAdd = (v: any) => {
    const newMember = v.value
    if (!newMember || groupMembers.some((m) => m.user_id === newMember.id))
      return

    setGroupMembers([
      ...groupMembers,
      { ...newMember, role: groupMembers.length === 0 ? 'OWNER' : 'MEMBER' },
    ])
  }

  const handleRoleChange = (memberId: number, checked: boolean) => {
    if (!checked) {
      const ownerCount = groupMembers.filter((m) => m.role === 'OWNER').length
      if (ownerCount <= 1) {
        return
      }
    }
    setGroupMembers(
      groupMembers.map((u) => ({
        ...u,
        role: u.id === memberId ? (checked ? 'OWNER' : 'MEMBER') : 'MEMBER',
      })),
    )
  }

  return (
    <>
      {data ? (
        <div>
          <InputGroup
            title='Add a Team Member'
            component={
              <Select
                components={{
                  ...SelectComponents,
                  Option: (props: any) => {
                    const { first_name, id, last_name } = props.value || {}
                    const isMember = groupMembers.find((v) => v.user_id === id)
                    return (
                      <components.Option {...props}>
                        <div>
                          <div className='d-flex justify-content-between'>
                            <div className='fw-semibold'>
                              {`${first_name} ${last_name}`}
                            </div>
                            <Checkbox label={''} checked={!!isMember} id={id} />
                          </div>
                        </div>
                      </components.Option>
                    )
                  },
                }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={[]}
                onChange={handleMemberAdd}
                options={teamMembers?.map((v) => ({
                  value: { ...v, user_id: v.id },
                }))}
              />
            }
          />

          <div className='mt-5'>
            <span className='fw-semibold fs-lg d-block mb-4'>Team Members</span>
            {!groupMembers?.length && (
              <span className='text-muted'>No team members</span>
            )}
            {groupMembers.map((v) => (
              <div className='d-flex justify-content-between bg-light150 px-4 py-2 rounded mb-4'>
                <div className='d-flex flex-column'>
                  <span className='fw-semibold'>
                    {v.first_name} {v.last_name}
                  </span>
                  <span>{v.email}</span>
                </div>
                <div className='d-flex align-items-center gap-6'>
                  <div>
                    <span className='fs-xss text-muted'>Group Owner</span>
                    <Switch
                      checked={v.role === 'OWNER'}
                      onChange={(checked) => handleRoleChange(v.id, checked)}
                    />
                  </div>
                  <Icon
                    className={classNames({
                      'cursor-pointer': v.role !== 'OWNER',
                      'opacity-50': v.role === 'OWNER',
                    })}
                    name='delete'
                    fill='danger'
                    onClick={() => {
                      if (
                        v.role === 'OWNER' &&
                        groupMembers.filter((m) => m.role === 'OWNER').length <=
                          1
                      ) {
                        return
                      }
                      openConfirm(
                        'Confirm remove user',
                        'Are you sure you wish to remove this user?',
                        (close) => {
                          close()
                          setGroupMembers(
                            groupMembers.filter((u) => v.id !== u.id),
                          )
                        },
                      )
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isFetching ? (
        <div className={'text-center'}>
          <Loader />
        </div>
      ) : (
        <div className={'text-center'}>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}
    </>
  )
}

export default AddRemoveMembers
